// Libs
import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import moment from 'moment';

// Services & Helpers
import DashboardService from 'services/DashboardService';
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';

// Components
import Loader from 'components/common/Loader';
import PageHeader from 'components/common/PageHeader';
import EditTodoModal from 'components/todos/EditTodoModal';
import Map from 'components/map/Map';

//-------------------------------------------------------------------------------------------------------------------

class DashboardPage extends React.Component {

    constructor(props) {
        super(props);

        this.editTodoModalRef = React.createRef();

        // Get last 4 quarters including this one
        this.quarters = [];
        const now = moment();
        let date = moment(now).startOf('quarter');
        let year = moment(now).year();
        let quarter = moment(now).quarter();
        for (let i = 0; i < 4; i++) {
            this.quarters.push({
                name: `Q${quarter}'${year - 2000}`,
                dateFrom: date.format('YYYY-MM-DD'),
                dateTo: moment(date).endOf('quarter').format('YYYY-MM-DD')
            });
            quarter--;
            if (quarter == 0) {
                quarter = 4;
                year--;
            }
            date.add(-3, 'months');
        }
        
        this.state = {
            isLoading: true,
            showVendorView: false,
            performanceSnapshotParams: {
                quarter: this.quarters[0].name
            },
            recentlyApprovedEnablementsParams: {
                language: 'English'
            }
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        GlobalStateService.setValues({ area: 'dashboard' });
        this.load();
    }

    async load() {
        const loginInfo = GlobalStateService.getValue('loginInfo');
        const showVendorView = !loginInfo.isAdmin || this.state.showVendorView;
        let instructors, cities;
        let devTasksSnapshotReq, bookingTodosReq, availableDatesReq,
            instructorChangesReq, vendorBookingTodosReq;

        // Trigger requests
        const instructorSnapshotReq = DashboardService.getInstructorSnapshot();
        if (showVendorView) {
            // Vendor only
            availableDatesReq = DashboardService.getAvailableDates();
            instructorChangesReq = DashboardService.getInstructorChanges();
            vendorBookingTodosReq = DashboardService.getVendorBookingTodos();
            await this.loadPerformanceSnapshot();
            await this.loadRecentlyApprovedEnablements();

            // Get all instructors
            instructors = await InstructorService.list({
                type: 'Approved',
                ignoreDeactivated: true
            });
            cities = InstructorService.groupByCity(instructors);
        } else {
            // Admin only
            devTasksSnapshotReq = DashboardService.getDevTasksSnapshot();
            bookingTodosReq = DashboardService.getBookingTodos();
        }

        // Gather requests and update UI
        this.setState({
            instructorSnapshot: await instructorSnapshotReq,
            devTasksSnapshot: await devTasksSnapshotReq,
            bookingTodos: await bookingTodosReq,
            availableDates: await availableDatesReq,
            instructorChanges: await instructorChangesReq,
            vendorBookingTodos: await vendorBookingTodosReq,
            cities,
            isLoading: false
        });
    }

    async loadPerformanceSnapshot() {
        const { quarter } = this.state.performanceSnapshotParams;
        const { dateFrom, dateTo } = this.quarters.find(q => q.name == quarter);
        const performanceSnapshot = await DashboardService.getPerformanceSnapshot({ dateFrom, dateTo });
        this.setState({ performanceSnapshot });
    }

    async loadRecentlyApprovedEnablements() {
        const { language } = this.state.recentlyApprovedEnablementsParams;
        const recentlyApprovedEnablements = await DashboardService.getRecentlyApprovedEnablements({ language });
        this.setState({ recentlyApprovedEnablements });
    }

    async showEditTodoModal(id) {
        await this.editTodoModalRef.current.show({
            todoID: id
        });
        this.load();
    }

    async addTodo(bookingType) {
        await this.editTodoModalRef.current.show({
            type: 'Booking',
            bookingType
        });
        this.load();
    }

    toggleVendorView() {
        const { showVendorView } = this.state;
        this.setState({
            showVendorView: !showVendorView,
            isLoading: true
        }, () => {
            this.load();
        });
    }

    render() {
        const { isLoading, bookingTodos, vendorBookingTodos } = this.state;
        const loginInfo = GlobalStateService.getValue('loginInfo');
        const showVendorView = !loginInfo.isAdmin || this.state.showVendorView;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>
            <div className="page dashboard-page">

                <PageHeader hasSideBar>
                    <h1>Dashboard</h1>

                    {loginInfo.isAdmin &&
                        <div className="buttons">

                            <button className="btn btn-primary" onClick={e => this.toggleVendorView()}>
                                {showVendorView ? 'Switch to Admin View' : 'Switch to Vendor View'}
                            </button>

                        </div>
                    }
                </PageHeader>

                <main>

                    <Container fluid>

                        {showVendorView && this.renderBookingTodosPanel(vendorBookingTodos, null, 'Action Summary', false)}

                        <Row className="mb-3">

                            <Col md="4">

                                {this.renderInstructorSnapshotPanel()}

                                {showVendorView ? <>
                                    {this.renderInstructorChangesPanel()}
                                    {this.renderReportsPanel()}
                                    {this.renderRecentlyApprovedEnablementsPanel()}
                                </> : <>
                                    {this.renderDevTasksSnapshotPanel()}
                                </>}

                            </Col>

                            <Col md="8">

                                {showVendorView ? <>
                                    {this.renderAvailableDatesPanel()}
                                    {this.renderPerformanceSnapshotPanel()}
                                    {this.renderMapPanel()}
                                </> : <>
                                    {this.renderBookingTodosPanel(bookingTodos.enquiry, 'Enquiry', 'Enquiries', true)}
                                    {this.renderBookingTodosPanel(bookingTodos.holdTentative, 'HoldTentative', 'Holds / Tentatives', true)}
                                    {this.renderBookingTodosPanel(bookingTodos.active, 'Active', 'Actives', true)}
                                </>}

                            </Col>

                        </Row>

                    </Container>

                </main>

            </div>

            <EditTodoModal ref={this.editTodoModalRef} />
        </>);
    }

    renderInstructorSnapshotPanel() {
        const { instructorSnapshot } = this.state;
        return (
            <section>
                <h2>Freelancer Pool Snapshot</h2>
                Number of Instructors: {instructorSnapshot.numApproved}<br />
                Number of Candidates: {instructorSnapshot.numCandidate}<br />
                Number of Prospects (Instructors): {instructorSnapshot.numProspectApproved}<br />
                Number of Prospects (Candidates): {instructorSnapshot.numProspectCandidate}
            </section>
        );
    }

    renderDevTasksSnapshotPanel() {
        const { devTasksSnapshot } = this.state;
        return (
            <section>
                <h2>Development</h2>

                <b>Currently working on</b><br />
                <ol>
                    {devTasksSnapshot.current.map(dt =>
                        <li key={dt.id}>
                            {dt.title}
                        </li>
                    )}
                </ol>
                <b>Recently completed</b><br />
                <ol>
                    {devTasksSnapshot.recentlyCompleted.map(dt =>
                        <li key={dt.id}>
                            {dt.title} ({moment(dt.dateCompleted).format('DD/MM/YYYY')})
                        </li>
                    )}
                </ol>
                <b>Queued requests</b><br />
                <ol>
                    {devTasksSnapshot.queued.map(dt =>
                        <li key={dt.id}>
                            {dt.title}
                        </li>
                    )}
                </ol>
            </section>
        );
    }

    renderBookingTodosPanel(todos, bookingType, title, isEditable) {
        return (
            <section>
                <h2>
                    {title}

                    {isEditable &&
                        <button type="button" className="btn btn-sm btn-primary" onClick={() => this.addTodo(bookingType)}>
                            <span className="fa fa-plus" />{' '}
                            Add
                        </button>
                    }
                </h2>

                {todos.length > 0 ?
                    <div className="sticky-table-container scrolling">
                        <table className={`table table-bordered sticky-table ${isEditable ? 'selectable' : ''}`}>
                            <thead>
                                <tr>
                                    {isEditable &&
                                        <th></th>
                                    }
                                    {bookingType == 'Enquiry' &&
                                        <th>Requester</th>
                                    }
                                    <th>LMS ID / Status</th>
                                    <th>Instructor</th>
                                    <th>Dates</th>
                                    <th>Course</th>
                                    <th>Customer</th>
                                    <th>Language</th>
                                    <th>vILT / ILT</th>
                                    <th>Address</th>
                                    <th>Comments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {todos.map(todo =>
                                    <tr key={todo.id} onClick={() => isEditable && this.showEditTodoModal(todo.id)}>
                                        {isEditable &&
                                            <td>
                                                {todo.isVendorVisible && 
                                                    <span className="fa-solid fa-eye" />
                                                }
                                            </td>
                                        }
                                        {bookingType == 'Enquiry' &&
                                            <td>{todo.bookingRequester}</td>
                                        }
                                        <td>{todo.bookingLMSIDOrStatus}</td>
                                        <td>{todo.bookingInstructor}</td>
                                        <td>{todo.bookingDates}</td>
                                        <td>{todo.bookingCourse}</td>
                                        <td>{todo.bookingCustomer}</td>
                                        <td>{todo.bookingLanguage}</td>
                                        <td>{todo.bookingModality}</td>
                                        <td>{todo.bookingAddress}</td>
                                        <td>{todo.lastCommentText}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div> :
                    <p className="empty-text">No items to show</p>
                }

            </section>
        );
    }

    renderAvailableDatesPanel() {
        const { availableDates } = this.state;
        return (
            <section>
                <h2>Available Dates</h2>
                <p>To end of year, by language</p>
                <ol>
                    {Object.keys(availableDates.numDatesByLanguage).map(language => {
                        const numDates = availableDates.numDatesByLanguage[language];
                        return (
                            <li key={language}>
                                {language}: {numDates}
                            </li>
                        );
                    })}
                </ol>
            </section>
        );
    }

    renderInstructorChangesPanel() {
        const { instructorChanges } = this.state;
        return (
            <section>
                <h2>AAI &amp; Candidate Changes</h2>

                <p>Newly Qualified Instructors</p>
                <ol>
                    {instructorChanges.newlyQualified.map(i =>
                        <li key={i.id}>
                            {i.firstName} {i.lastName}
                        </li>
                    )}
                    {instructorChanges.newlyQualified.length == 0 &&
                        <li>No instructors</li>
                    }
                </ol>
                <p>Newly Joined AAIs</p>
                <ol>
                    {instructorChanges.newlyJoinedApproved.map(i =>
                        <li key={i.id}>
                            {i.firstName} {i.lastName}
                        </li>
                    )}
                    {instructorChanges.newlyJoinedApproved.length == 0 &&
                        <li>No instructors</li>
                    }
                </ol>
                <p>Newly Joined Candidates</p>
                <ol>
                    {instructorChanges.newlyJoinedCandidate.map(i =>
                        <li key={i.id}>
                            {i.firstName} {i.lastName}
                        </li>
                    )}
                    {instructorChanges.newlyJoinedCandidate.length == 0 &&
                        <li>No instructors</li>
                    }
                </ol>
                <p>Recently Deactivated AAIs</p>
                <ol>
                    {instructorChanges.recentlyDeactivatedApproved.map(i =>
                        <li key={i.id}>
                            {i.firstName} {i.lastName}
                        </li>
                    )}
                    {instructorChanges.recentlyDeactivatedApproved.length == 0 &&
                        <li>No instructors</li>
                    }
                </ol>
                <p>Recently Deactivated Candidates</p>
                <ol>
                    {instructorChanges.recentlyDeactivatedCandidate.map(i =>
                        <li key={i.id}>
                            {i.firstName} {i.lastName}
                        </li>
                    )}
                    {instructorChanges.recentlyDeactivatedCandidate.length == 0 &&
                        <li>No instructors</li>
                    }
                </ol>
            </section>
        );
    }

    renderReportsPanel() {
        return (
            <section>
                <h2>Reports</h2>
                <div className="btn-stack w-100">
                    <Link className="btn btn-secondary mb-3" to="/reports/availability">
                        Availability
                    </Link>
                    <Link className="btn btn-secondary mb-3" to="/reports/courses-in-ramping">
                        Courses in ramping
                    </Link>
                    <Link className="btn btn-secondary" to="/reports/bookings">
                        Bookings
                    </Link>
                </div>
            </section>
        );
    }

    renderRecentlyApprovedEnablementsPanel() {
        const { recentlyApprovedEnablementsParams, recentlyApprovedEnablements } = this.state;

        const languages = [
            'English',
            'French',
            'German',
            'Italian',
            'Spanish',
            'Hebrew',
            'Portuguese'
        ];

        return (
            <section>
                <h2>Recently approved enablements</h2>
                <select
                    className="form-control mb-3"
                    value={recentlyApprovedEnablementsParams.language || ''}
                    onChange={e => {
                        const recentlyApprovedEnablementsParams = { ...this.state.recentlyApprovedEnablementsParams };
                        recentlyApprovedEnablementsParams.language = e.target.value;
                        this.setState({ recentlyApprovedEnablementsParams }, () => {
                            this.loadRecentlyApprovedEnablements();
                        })
                    }}
                >
                    {languages.map(l =>
                        <option key={l}>{l}</option>
                    )}
                </select>

                {recentlyApprovedEnablements.rows.length > 0 ?
                    <table className="table table-bordered mb-0">
                        <thead>
                            <tr>
                                <th>Course</th>
                                <th>Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentlyApprovedEnablements.rows.map(rae =>
                                <tr key={rae.courseName}>
                                    <td>{rae.courseName}</td>
                                    <td>{rae.quantity}</td>
                                </tr>
                            )}
                        </tbody>
                    </table> :
                    <p className="empty-text mb-0">
                        No recent approved enablements
                        for this language
                    </p>
                }
            </section>
        );
    }

    renderPerformanceSnapshotPanel() {
        const { performanceSnapshot, performanceSnapshotParams } = this.state;
        return (
            <section>
                <h2>Performance Snapshot</h2>

                <p>CSAT Average Score (YtD):  {Math.round(performanceSnapshot.averageCSATScore * 100) / 100}</p>

                <p>Top Performers</p>

                <select
                    className="form-control mb-3"
                    value={performanceSnapshotParams.quarter || ''}
                    onChange={e => {
                        const performanceSnapshotParams = { ...this.state.performanceSnapshotParams };
                        performanceSnapshotParams.quarter = e.target.value;
                        this.setState({ performanceSnapshotParams }, () => {
                            this.loadPerformanceSnapshot();
                        })
                    }}
                >
                    {this.quarters.map(q =>
                        <option key={q.name} value={q.name}>{q.name}</option>
                    )}
                </select>

                <ol>
                    {performanceSnapshot.topPerformers.map(tp =>
                        <li key={tp.id}>
                            {tp.firstName} {tp.lastName}
                            {' '}
                            ({tp.numDeliveries} {tp.numDeliveries == 1 ? 'delivery' : 'deliveries'},
                            {' '}
                            average score {Math.round(tp.averageCSATScore * 100) / 100})
                        </li>
                    )}
                    {performanceSnapshot.topPerformers == 0 && 
                        <li>No instructors</li>
                    }
                </ol>

            </section>
        );
    }

    renderMapPanel() {
        const { cities, isLoading } = this.state;
        
        return (
            <section>
                <h2>Map</h2>
                {isLoading ?
                    <Loader /> :
                    <Map
                        googleMapURL="https://maps.googleapis.com/maps/api/js?key=AIzaSyBRkRLRyaGMKrRkSTyVOdpfxVrtSsdUeNQ&libraries=&v=weekly"
                        loadingElement={<div style={{ height: '100%' }} />}
                        containerElement={<React.Fragment />}
                        mapElement={<div style={{ height: `calc(100vh - 200px)`, minHeight: 500 }} />}
                        cities={cities}
                    />
                }
            </section>
        );
    }

    renderBookingFeedbackSnapshotsPanel() {
        return (<>

            <h2>Booking Feedback Snapshots</h2>
            {/*
            <ul>
                {performanceSnapshot.bookingFeedbackSnapshots.map((bookingFeedback, index) =>
                    <li key={index}>
                        {bookingFeedback}
                    </li>
                )}
            </ul>
            */}
        </>);
    }

}

export default withRouter(DashboardPage);